import React from "react";
import { useAlert } from 'react-alert';
import Button from "../common_components/Button";

const TIERS = [
    { key: 'explorer', name: 'Explorer', required: 0, reward: 5, friendEarns: 5 },
    { key: 'connector', name: 'Connector', required: 3, reward: 7, friendEarns: 5 },
    { key: 'advocate', name: 'Advocate', required: 7, reward: 10, friendEarns: 5 },
    { key: 'partner', name: 'Partner', required: 15, reward: 15, friendEarns: 5 },
];

function ReferralsTable(props) {
    const alert = useAlert();

    return (
        <div className="referrals-page-container">
            {/* Header */}
            <div className="referrals-page-header">
                <h2 className="referrals-page-title">Referrals</h2>
                {props.referralLink ?
                    <Button size="small" color="blue" onClick={() => { props.copyLink() }}>Copy link</Button>
                    :
                    <Button size="small" color="blue" onClick={() => { props.getLink() }}>Get link</Button>
                }
            </div>

            {/* Info Pills */}
            <div className="referrals-info-pills">
                <div className="referrals-info-pill">You & your friend each get <strong>$5</strong></div>
                <div className="referrals-info-pill">Up to <strong>$15 per referral</strong> with tiers</div>
                <div className="referrals-info-pill"><strong>Extra mission bonuses</strong> on select missions</div>
            </div>

            {/* Progress Section */}
            <ReferralsProgressSection tierInfo={props.tierInfo} />

            {/* Tier Table */}
            <ReferralsTierTable currentTier={props.tierInfo?.tier_key} />

            {/* Note */}
            <p className="referrals-note">
                Referral bonuses are paid when referred users complete an interview mission. Survey missions will not count. <a href="https://desparkwiki.notion.site/how-do-referrals-work?source=despark_app" target="_blank" className="referrals-note-link">View referral program details</a>.
            </p>

            {/* Your Link */}
            <div className="referrals-link-section">
                <h3 className="referrals-section-title">Your Link</h3>
                {props.referralLink ?
                    <p className="referrals-link-text">{props.referralLink}</p>
                    :
                    <p className="referrals-link-text referrals-link-placeholder">Generate a link to start referring friends</p>
                }
            </div>

            {/* Referral History */}
            <div className="referrals-history-section">
                <h3 className="referrals-section-title">Referral History</h3>
                {props.referrals.length > 0 && props.referrals.map((referral) => (
                    <ReferralRow referral={referral} key={referral.id} />
                ))}
                {props.referrals.length === 0 &&
                    <p className="referrals-history-empty">No referrals yet</p>
                }
            </div>
        </div>
    );
}

function ReferralsProgressSection({ tierInfo }) {
    if (!tierInfo) return null;

    const { completed_last_year, referrals_to_next_tier } = tierInfo;

    // Calculate progress toward next tier
    let nextTierMin;
    let progressPercent;
    let rightLabel;
    let rightSubLabel;

    if (referrals_to_next_tier === null) {
        // Already at max tier
        nextTierMin = completed_last_year;
        progressPercent = 100;
        rightLabel = `${completed_last_year}`;
        rightSubLabel = "(max level)";
    } else {
        nextTierMin = completed_last_year + referrals_to_next_tier;
        progressPercent = nextTierMin > 0
            ? Math.min(Math.max((completed_last_year / nextTierMin) * 100, 2), 100)
            : 0;
        rightLabel = `${completed_last_year}/${nextTierMin}`;
        rightSubLabel = "(for next level)";
    }

    return (
        <div className="referrals-progress-section">
            <div className="referrals-progress-left">
                <span className="referrals-progress-title">Your</span>
                <span className="referrals-progress-title">Referrals</span>
                <span className="referrals-progress-subtitle">(past year)</span>
            </div>
            <div className="referrals-progress-bar">
                <div
                    className="referrals-progress-bar-fill"
                    style={{ width: `${progressPercent}%` }}
                />
            </div>
            <div className="referrals-progress-right">
                <span className="referrals-progress-value">{rightLabel}</span>
                <span className="referrals-progress-subtitle">{rightSubLabel}</span>
            </div>
        </div>
    );
}

function ReferralsTierTable({ currentTier }) {
    return (
        <table className="referrals-tier-table">
            <thead>
                <tr>
                    <th>Your Level</th>
                    <th>Req. Referrals</th>
                    <th>Your Referral Reward</th>
                    <th>Your Friend Earns</th>
                </tr>
            </thead>
            <tbody>
                {TIERS.map(tier => (
                    <tr key={tier.key} className={currentTier === tier.key ? 'current-tier' : ''}>
                        <td>{tier.name}</td>
                        <td>{tier.required}</td>
                        <td>${tier.reward}</td>
                        <td>${tier.friendEarns}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function ReferralRow({ referral }) {
    const getStatusText = () => {
        switch (referral.status) {
            case "completed":
                return "Complete";
            case "signed_up":
                return "Awaiting interview";
            case "disallowed_country":
                return "Disallowed country";
            case "partial_complete":
                return "Complete; wrong mission";
            default:
                return referral.status;
        }
    };

    return (
        <div className="referrals-history-row">
            <div className="referrals-history-left">
                <div className="referrals-history-email">{referral.name}</div>
                {referral.completed_mission_name && (
                    <div className="referrals-history-mission">
                        Mission: "{referral.completed_mission_name}"
                    </div>
                )}
                <div className="referrals-history-date">{referral.date}</div>
            </div>
            <div className="referrals-history-right">
                <div className="referrals-history-status">{getStatusText()}</div>
                <div className={`referrals-history-amount ${referral.status}`}>
                    ${(referral.status == "completed" && referral.mission_bonus_amount
                        ? (referral.mission_bonus_amount * 1.0 + referral.amount * 1.0)
                        : (referral.amount * 1.0)
                    ).toFixed(2)}
                </div>
            </div>
        </div>
    );
}

export default ReferralsTable;
